import { KiteCard, KiteIcon } from '@kite/react-kite';
import React from 'react';
import { SmartLink } from '../SmartLink/SmartLink';
import { getVideoId } from '../../shared/getVideoId';
import './YoutubeCard.scss';

export type YoutubeCardProps = {
    alignRight: boolean;
    articleLink: {
        title: string;
        uri: string;
    };
    subtitle?: string;
    eyebrow?: string;
    showPostDate: boolean;
    showRuntime: boolean;
    aspectRatio?: string;
    videoTitle?: string;
    showViews: boolean;
    description?: string;
    sectionTitle?: string;
    youtubeSrcUrl?: string;
    parent: `article` | `landing`;
};

const YoutubeCard = ({ inputs }: { inputs: YoutubeCardProps }) => {
    const {
        alignRight,
        articleLink,
        aspectRatio,
        description,
        eyebrow,
        sectionTitle,
        showPostDate,
        showRuntime,
        showViews,
        subtitle,
        videoTitle,
        youtubeSrcUrl,
        parent,
    } = inputs;

    const videoId = getVideoId(youtubeSrcUrl);

    const videoWrapperClasses = `youtube-card__video-wrapper youtube-card__video-wrapper youtube-card__video-wrapper--${aspectRatio} youtube-card__video-wrapper--${parent} ${
        alignRight ? `youtube-card__video-wrapper--right-align` : ``
    }`;

    if (articleLink?.uri === `route:<nolink>`) {
        articleLink.uri = ``;
    }

    if (!videoId) {
        return (
            <section
                className={`youtube-card ${
                    parent === `landing` ? `kite-container` : ``
                }`}
            >
                <p className="youtube-card__error">
                    <strong>Error: </strong>Could not parse Youtube URL
                </p>
            </section>
        );
    }

    return (
        <section
            className={`youtube-card youtube-card--${parent} ${
                parent === `landing` ? `kite-container` : ``
            }`}
        >
            <div className="youtube-card__heading">
                {sectionTitle && (
                    <h2 className="youtube-card__title">{`${sectionTitle}`}</h2>
                )}
                {subtitle && (
                    <p className="youtube-card__subtitle">{`${subtitle}`}</p>
                )}
            </div>
            <div>
                <KiteCard
                    className={`youtube-card__card youtube-card__card--${aspectRatio}`}
                >
                    <div
                        className={`youtube-card__content youtube-card__content--${aspectRatio} ${
                            alignRight
                                ? `youtube-card__content--right-align`
                                : ``
                        }`}
                    >
                        {(!alignRight ||
                            (alignRight && aspectRatio === `16_9`)) && (
                            <div
                                className={
                                    videoWrapperClasses +
                                    ` youtube-card__video-wrapper--top`
                                }
                            >
                                <iframe
                                    className={`youtube-card__video youtube-card__video--${aspectRatio}`}
                                    src={`https://www.youtube.com/embed/${videoId}?enablejsapi=1`}
                                    title={videoTitle ?? `YouTube video player`}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            </div>
                        )}
                        <div className="youtube-card__copy">
                            {eyebrow && (
                                <small className="youtube-card__eyebrow">{`${eyebrow}`}</small>
                            )}
                            {videoTitle && (
                                <h3 className="youtube-card__video-title">{`${videoTitle}`}</h3>
                            )}
                            {description && (
                                <p className="youtube-card__description">{`${description}`}</p>
                            )}
                            {articleLink?.uri && (
                                <SmartLink
                                    url={articleLink?.uri}
                                    newWindow
                                    classNames={[`youtube-card__link`]}
                                >
                                    {articleLink?.title}
                                    {articleLink.uri.includes(`https://`) ? (
                                        <KiteIcon
                                            icon="ki-linkout"
                                            size="1rem"
                                        />
                                    ) : (
                                        <KiteIcon
                                            icon="ki-arrow-right"
                                            size="1rem"
                                        />
                                    )}
                                </SmartLink>
                            )}
                        </div>
                        {alignRight && (
                            <div className={videoWrapperClasses}>
                                <iframe
                                    className={`youtube-card__video youtube-card__video--${aspectRatio}`}
                                    src={`https://www.youtube.com/embed/${videoId}?enablejsapi=1`}
                                    title={videoTitle ?? `YouTube video player`}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            </div>
                        )}
                    </div>
                </KiteCard>
            </div>
        </section>
    );
};

export default YoutubeCard;
